import React, { useState, useRef, useCallback } from 'react';
import { NodeProps } from '@xyflow/react';
import { useShallow } from 'zustand/react/shallow';
import clsx from 'clsx';
import { Editor, EditorEvents } from '@tiptap/react';
import { AppState, useCanvasStore } from 'iron/common/report/canvas/store';
import { formatDate } from 'common/util/date';
import { TipTapEditor } from 'common/components/tiptap-editor';
import styles from './index.module.styl';
import { StickyNoteNodeType } from './types';
const MAX_CHARACTERS = 1000;
const selector = (state: AppState) => {
  var _state$canvas, _state$ghostNode;
  return {
    isViewOnly: !((_state$canvas = state.canvas) !== null && _state$canvas !== void 0 && _state$canvas.can_update_basic),
    ghostNodeId: ((_state$ghostNode = state.ghostNode) === null || _state$ghostNode === void 0 ? void 0 : _state$ghostNode.id) || "",
    deleteNode: state.deleteNode,
    setActiveMetricNodeId: state.setActiveMetricNodeId,
    setStickyNoteNodeContent: state.setStickyNoteNodeContent
  };
};
export function StickyNoteNode(_ref: NodeProps<StickyNoteNodeType>) {
  let {
    id,
    data,
    selected,
    dragging
  }: NodeProps<StickyNoteNodeType> = _ref;
  const {
    isViewOnly,
    setActiveMetricNodeId,
    setStickyNoteNodeContent,
    ghostNodeId,
    deleteNode
  } = useCanvasStore(useShallow(selector));
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [focused, setFocused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const ref = useRef<Editor | null>(null);
  const handleNodeClick = () => {
    setActiveMetricNodeId(null);
    if (!focused && selected) {
      var _ref$current;
      (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.chain().focus("end").run();
    }
  };
  const {
    author,
    markdown,
    lastModified
  } = data;
  const lastModifiedDate = formatDate(new Date(lastModified), {
    unit: "day",
    customFormatting: {
      day: "M/DD/YYYY"
    }
  });
  const handleMouseEnter = useCallback(() => {
    timerRef.current = setTimeout(() => setIsHovered(true), 150);
  }, [setIsHovered]);
  const handleMouseLeave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setIsHovered(false);
  }, [setIsHovered]);
  const handleContentUpdate = useCallback<(editorProps: EditorEvents[`update`]) => void>(_ref2 => {
    let {
      editor
    } = _ref2;
    const newContent = editor.getHTML();
    if (newContent.length <= MAX_CHARACTERS) {
      setStickyNoteNodeContent(id, newContent);
    }
  }, [setStickyNoteNodeContent, id]);
  const handleContentFocus = useCallback<(editorProps: EditorEvents[`focus`]) => void>(_ref3 => {
    let {
      editor
    } = _ref3;
    setFocused(editor.isFocused);
  }, [setFocused]);
  const handleContentBlur = useCallback<(editorProps: EditorEvents[`blur`]) => void>(_ref4 => {
    let {
      editor
    } = _ref4;
    setFocused(editor.isFocused);
  }, [setFocused]);
  const handleDelete = useCallback(() => {
    deleteNode(id);
  }, [deleteNode, id]);
  const isGhostNode = id === ghostNodeId;
  return <div tabIndex={0} key={id} className={clsx(styles.stickyNoteNode, {
    [styles.ghost]: isGhostNode
  })} onClick={handleNodeClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-sentry-component="StickyNoteNode" data-sentry-source-file="index.tsx">
      <div className={styles.contentWrapper}>
        <TipTapEditor ref={ref} className={clsx({
        [styles.textEditor]: true,
        [styles.notSelected]: !selected || dragging,
        [styles.notFocused]: !focused
      })} autofocus={!markdown && !isGhostNode} onFocus={handleContentFocus} onBlur={handleContentBlur} canEdit={!isViewOnly} content={markdown} placeholder='Add text' alwaysShowPlaceHolder onDelete={handleDelete} onUpdate={handleContentUpdate} data-sentry-element="TipTapEditor" data-sentry-source-file="index.tsx" />
        <div className={clsx({
        [styles.signature]: true,
        [styles.showSignature]: !isGhostNode && (isHovered || selected)
      })}>
          <span>{author || "Unknown"}</span> - <span>{lastModifiedDate || ""}</span>
        </div>
      </div>
    </div>;
}